import { useCallback, useEffect } from "react";
import {
  NavigateOptions,
  useLocation,
  useNavigate,
  useNavigationType,
} from "react-router-dom";

// Copia del historial del navegador dentro de la app (solo las entradas que
// hemos visto), para saber de dónde venimos sin tocar `window.history`.
type Entry = { key: string; pathname: string };
let entries: Entry[] = [];
let index = -1;

/**
 * Montar una vez dentro del Router: sigue cada cambio de ruta para que
 * `useGoBack` sepa cuál es la entrada anterior del historial.
 */
export const NavigationHistoryTracker = (): null => {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    const entry = { key: location.key, pathname: location.pathname };
    if (entries[index]?.key === entry.key) return;

    if (navigationType === "PUSH") {
      entries = [...entries.slice(0, index + 1), entry];
      index = entries.length - 1;
    } else if (navigationType === "REPLACE" && index >= 0) {
      entries[index] = entry;
    } else {
      // POP (flechas del navegador) o carga inicial.
      const found = entries.findIndex((e) => e.key === entry.key);
      if (found >= 0) {
        index = found;
      } else {
        entries = [entry];
        index = 0;
      }
    }
  }, [location.key, location.pathname, navigationType]);

  return null;
};

/**
 * Botón "atrás": si la página anterior del historial es `fallback`, hace lo
 * mismo que la flecha del navegador (conserva filtros, paginación y scroll, y
 * tira de la caché). Si no (enlace directo, recarga, otro origen), navega a
 * `fallback` como antes.
 */
export const useGoBack = () => {
  const navigate = useNavigate();

  return useCallback(
    (fallback: string, options?: NavigateOptions) => {
      const previous = entries[index - 1];
      const target = fallback.split(/[?#]/)[0];
      if (previous && previous.pathname === target) {
        navigate(-1);
      } else {
        navigate(fallback, options);
      }
    },
    [navigate]
  );
};
